import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getGlobalAccessToken, getGlobalAuthInstance } from '../utils/authUtils';

// Global toast function - will be set by the app
let globalShowToast: ((message: string, severity?: 'error' | 'warning' | 'info' | 'success') => void) | null = null;

// Track when we last showed an auth error to prevent spam
let lastAuthErrorTime = 0;
const AUTH_ERROR_DEBOUNCE_MS = 5000; // Only show one auth error per 5 seconds

export const setGlobalToast = (toastFn: (message: string, severity?: 'error' | 'warning' | 'info' | 'success') => void) => {
  globalShowToast = toastFn;
};

// Removed FailedRequest interface - no longer needed since we're not manually refreshing tokens

class ApiClient {
  private readonly apiClient: AxiosInstance;

  constructor(baseURL: string) {
    this.apiClient = axios.create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });

    this.apiClient.interceptors.request.use(this.addAuthHeader);
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => this.handleTokenExpiration(error)
    );
    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 0 && error.message?.includes('CORS')) {
          console.error('CORS Error:', error);
          // Handle CORS error - could redirect to error page or retry
        }
        return Promise.reject(error);
      }
    );
  }

  // Add Authorization header to every request
  private readonly addAuthHeader = (
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig => {
    let accessToken = null;

    // First try to get token from global auth getter (preferred method)
    accessToken = getGlobalAccessToken();

    // If global getter didn't work, try localStorage methods
    if (!accessToken) {
      try {
        // react-oidc-context stores user data under 'oidc.user' key
        const oidcUser = localStorage.getItem('oidc.user');
        if (oidcUser) {
          const userData = JSON.parse(oidcUser);
          accessToken = userData.access_token;
        }

        // If not found in oidc.user, try other possible keys
        if (!accessToken) {
          // Try the key pattern that react-oidc-context might use
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('oidc.user') || key.includes('user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  accessToken = data.access_token;
                  break;
                }
              } catch (e) {
                // Continue to next key
              }
            }
          }
        }

        // Fallback to manual token storage
        if (!accessToken) {
          accessToken = localStorage.getItem("access_token");
        }
      } catch (error) {
        console.error('Error getting access token:', error);
        // Final fallback
        accessToken = localStorage.getItem("access_token");
      }
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      console.warn('❌ No access token found for API request');
    }

    return config;
  };

  // Handle token expiration and authentication errors
  private readonly handleTokenExpiration = async (
    error: AxiosError
  ): Promise<AxiosResponse | void> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Check for any authentication errors (401, 403) or specific error messages
    const isAuthError = error.response &&
      (error.response.status === 401 ||
        error.response.status === 403 ||
        (error.response.data as any)?.errorCode === 'TOKEN_EXPIRED' ||
        (error.response.data as any)?.detail === 'Token has expired');

    if (isAuthError && !originalRequest._retry) {
      // Mark as retried to prevent infinite loops
      originalRequest._retry = true;

      // Try to refresh the token using react-oidc-context
      const authInstance = getGlobalAuthInstance();
      if (authInstance && authInstance.signinSilent) {
        try {
          console.log('Attempting silent token refresh...');
          await authInstance.signinSilent();
          console.log('Token refresh successful, retrying original request');

          // Retry the original request with the new token
          return this.apiClient.request(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed - refresh token likely expired:', refreshError);

          // Refresh token has expired - redirect to login
          const now = Date.now();
          if ((now - lastAuthErrorTime) > AUTH_ERROR_DEBOUNCE_MS) {
            lastAuthErrorTime = now;

            // Show toast message
            if (globalShowToast) {
              globalShowToast("Your session has expired. Please sign in again.", "warning");
            }

            // Redirect to signin after a brief delay to show the message
            setTimeout(() => {
              window.location.href = '/signin';
            }, 1000);
          }
        }
      } else {
        // No auth instance available, redirect to login
        const now = Date.now();
        if ((now - lastAuthErrorTime) > AUTH_ERROR_DEBOUNCE_MS) {
          lastAuthErrorTime = now;

          if (globalShowToast) {
            globalShowToast("Your session has expired. Please sign in again.", "warning");
          }

          setTimeout(() => {
            window.location.href = '/signin';
          }, 1000);
        }
      }
    }

    // Only transform authentication errors - let all other errors pass through unchanged
    if (isAuthError) {
      return Promise.reject(new Error(error?.message || "Authentication failed"));
    }

    // For all non-auth errors, pass through the original error unchanged
    return Promise.reject(error);
  };

  // Expose apiClient instance
  public getClient(): AxiosInstance {
    return this.apiClient;
  }
}

export default ApiClient;
